import React, { useState } from 'react'
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Text,
  Button,
  useColorModeValue,
  Spinner,
  Select,
  IconButton,
  Tooltip,
} from '@chakra-ui/react'
import { SearchIcon, DownloadIcon } from '@chakra-ui/icons'
import Card from 'components/card/Card'
import { useGetTickets } from 'queries/ticket.query'
import { useGetEvents } from 'queries/event.query'
import { getStorageImageUrl } from 'utils/imageUrl'

export default function TicketsTable() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedEventId, setSelectedEventId] = useState('')
  const [selectedTierId, setSelectedTierId] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const [downloadingId, setDownloadingId] = useState(null)

  const { data: eventsData } = useGetEvents(1, 100)
  const events = eventsData?.data || []
  const currentEvent = events.find((e) => String(e.id) === String(selectedEventId))
  const tiers = currentEvent?.ticket_tiers || []

  const { data: ticketsData, isLoading } = useGetTickets(page, pageSize, searchQuery, {
    event_id: selectedEventId,
    tier_id: selectedTierId,
    status: selectedStatus,
  })

  const handleSearch = (e) => {
    setSearchQuery(e.target.value)
    setPage(1)
  }

  const handleDownloadPdf = async (pdfPath, ticketCode, ticketId) => {
    if (!pdfPath) return;
    const fullUrl = getStorageImageUrl(pdfPath);
    if (!fullUrl) return;
    const filename = `Ticket_${ticketCode || ticketId || 'admission'}.pdf`;

    setDownloadingId(ticketId);
    try {
      const response = await fetch(fullUrl);
      if (!response.ok) throw new Error('Network error');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 300);
    } catch (err) {
      // Fallback: direct download link
      const link = document.createElement('a');
      link.href = fullUrl;
      link.download = filename;
      link.setAttribute('download', filename);
      link.target = '_self';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
      }, 300);
    } finally {
      setDownloadingId(null);
    }
  }

  const textColor = useColorModeValue('secondaryGray.900', 'white')

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card
        direction="column"
        w="100%"
        px="0px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
      >
        <Flex px="25px" justify="space-between" mb="20px" align="center" wrap="wrap" gap={3}>
          <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
            Sold Tickets
          </Text>
          <Flex gap={3} wrap="wrap" align="center" flex="1" justify={{ base: 'flex-start', md: 'flex-end' }}>
            <InputGroup maxW={{ base: '100%', md: '220px' }} size="sm">
              <InputLeftElement children={<SearchIcon color="gray.400" />} />
              <Input
                placeholder="Search code, name, email..."
                value={searchQuery}
                onChange={handleSearch}
                borderRadius="12px"
              />
            </InputGroup>

            <Select
              size="sm"
              maxW="200px"
              borderRadius="12px"
              value={selectedEventId}
              onChange={(e) => {
                setSelectedEventId(e.target.value)
                setSelectedTierId('')
                setPage(1)
              }}
            >
              <option value="">All Events</option>
              {events.map((evt) => (
                <option key={evt.id} value={evt.id}>
                  {evt.title}
                </option>
              ))}
            </Select>

            {tiers.length > 0 && (
              <Select
                size="sm"
                maxW="160px"
                borderRadius="12px"
                value={selectedTierId}
                onChange={(e) => {
                  setSelectedTierId(e.target.value)
                  setPage(1)
                }}
              >
                <option value="">All Tiers</option>
                {tiers.map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {tier.name}
                  </option>
                ))}
              </Select>
            )}

            <Select
              size="sm"
              maxW="180px"
              borderRadius="12px"
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value)
                setPage(1)
              }}
            >
              <option value="">All Statuses</option>
              <option value="valid">Valid (Unchecked)</option>
              <option value="checked_in">Checked In</option>
              <option value="refunded">Refunded / Cancelled</option>
            </Select>

            {(selectedEventId || selectedTierId || selectedStatus || searchQuery) && (
              <Button
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={() => {
                  setSelectedEventId('')
                  setSelectedTierId('')
                  setSelectedStatus('')
                  setSearchQuery('')
                  setPage(1)
                }}
              >
                Reset
              </Button>
            )}
          </Flex>
        </Flex>

        <Box overflowX="auto">
          <Table variant="simple" color="gray.500" mb="24px">
            <Thead>
              <Tr>
                <Th>Ticket Code</Th>
                <Th>Event</Th>
                <Th>Tier/Category</Th>
                <Th>Seat / Row</Th>
                <Th>Attendee / Order Email</Th>
                <Th>Price</Th>
                <Th>Status</Th>
                <Th>PDF</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading ? (
                <Tr>
                  <Td colSpan={8} textAlign="center" py={10}>
                    <Spinner size="xl" />
                  </Td>
                </Tr>
              ) : ticketsData?.data?.length === 0 ? (
                <Tr>
                  <Td colSpan={8} textAlign="center" py={10}>
                    No tickets found.
                  </Td>
                </Tr>
              ) : (
                ticketsData?.data?.map((ticket) => {
                  const orderStatus = (ticket.order?.payment_status || '').toLowerCase()
                  const ticketStatus = (ticket.status || ticket.effective_status || '').toLowerCase()
                  const isRefunded = ticketStatus === 'refunded' || orderStatus === 'refunded'
                  const isCancelled = ticketStatus === 'cancelled' || orderStatus === 'cancelled'
                  const isVoid = isRefunded || isCancelled

                  return (
                    <Tr 
                      key={ticket.id}
                      opacity={isVoid ? 0.75 : 1}
                      bg={isVoid ? 'blackAlpha.50' : undefined}
                    >
                      <Td fontWeight="bold" color={isVoid ? 'gray.500' : textColor}>
                        <Flex align="center" gap={1.5}>
                          <Text as={isVoid ? 's' : undefined}>{ticket.ticket_code}</Text>
                          {isRefunded && (
                            <Badge colorScheme="red" variant="subtle" fontSize="9px" px={1.5} py={0.5} borderRadius="sm">
                              VOID
                            </Badge>
                          )}
                        </Flex>
                      </Td>
                      <Td>{ticket.event?.title}</Td>
                      <Td>
                        <Badge colorScheme={isVoid ? "gray" : "blue"} textTransform="uppercase" px={2} py={1} borderRadius="md">
                          {ticket.tier_name || ticket.ticket_tier?.name || ticket.ticketTier?.name || 'General'}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme={isVoid ? "gray" : "purple"} variant="subtle" px={2} py={1} borderRadius="md" fontFamily="mono">
                          {ticket.seat_info || (ticket.seat_guid && !ticket.seat_guid.startsWith('tier-') ? ticket.seat_guid : 'General / Standing')}
                        </Badge>
                      </Td>
                      <Td>
                        <Text color={isVoid ? "gray.500" : undefined}>{ticket.attendee_name || '-'}</Text>
                        <Flex align="center" gap={1.5} wrap="wrap">
                          <Text fontSize="sm" color="gray.400">{ticket.order?.customer_email}</Text>
                          {isRefunded && (
                            <Badge colorScheme="red" variant="outline" fontSize="9px" px={1} borderRadius="sm">
                              Order Refunded
                            </Badge>
                          )}
                          {isCancelled && !isRefunded && (
                            <Badge colorScheme="red" variant="outline" fontSize="9px" px={1} borderRadius="sm">
                              Order Cancelled
                            </Badge>
                          )}
                        </Flex>
                      </Td>
                      <Td>
                        {(() => {
                          const paid = Number(ticket.price_paid !== undefined ? ticket.price_paid : (ticket.ticket_tier?.price ?? ticket.ticketTier?.price ?? 0))
                          const orig = Number(ticket.original_price !== undefined ? ticket.original_price : (ticket.ticket_tier?.price ?? ticket.ticketTier?.price ?? 0))
                          const hasDiscount = orig > paid && orig > 0
                          return (
                            <Box opacity={isVoid ? 0.7 : 1}>
                              <Text fontWeight="bold" color={isVoid ? 'gray.400' : textColor} as={isVoid ? 's' : undefined}>
                                {paid === 0 ? '€0.00 (Free)' : `€${paid.toFixed(2)}`}
                              </Text>
                              {isRefunded ? (
                                <Text fontSize="xs" color="red.500" fontWeight="bold">
                                  Refunded
                                </Text>
                              ) : isCancelled ? (
                                <Text fontSize="xs" color="red.500" fontWeight="bold">
                                  Cancelled
                                </Text>
                              ) : hasDiscount ? (
                                <Text fontSize="xs" color="gray.400" as="s">
                                  €{orig.toFixed(2)}
                                </Text>
                              ) : null}
                            </Box>
                          )
                        })()}
                      </Td>
                      <Td>
                        {isRefunded ? (
                          <Badge colorScheme="red" px={2} py={1} borderRadius="md" fontWeight="bold">
                            Refunded
                          </Badge>
                        ) : isCancelled ? (
                          <Badge colorScheme="red" px={2} py={1} borderRadius="md" fontWeight="bold">
                            Cancelled
                          </Badge>
                        ) : ticket.is_checked_in ? (
                          <Badge colorScheme="green" px={2} py={1} borderRadius="md">
                            Checked In
                          </Badge>
                        ) : (
                          <Badge colorScheme="orange" px={2} py={1} borderRadius="md">
                            Valid
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        {isVoid ? (
                          <Tooltip label="Ticket is void (Refunded/Cancelled). Download disabled.">
                            <IconButton
                              icon={<DownloadIcon />}
                              colorScheme="gray"
                              variant="ghost"
                              isDisabled
                              aria-label="Download PDF disabled"
                            />
                          </Tooltip>
                        ) : ticket.pdf_path ? (
                          <IconButton
                            icon={<DownloadIcon />}
                            colorScheme="brand"
                            variant="ghost"
                            isLoading={downloadingId === ticket.id}
                            onClick={() => handleDownloadPdf(ticket.pdf_path, ticket.ticket_code, ticket.id)}
                            aria-label="Download PDF"
                          />
                        ) : (
                          <Text fontSize="xs">No PDF</Text>
                        )}
                      </Td>
                    </Tr>
                  )
                })
              )}
            </Tbody>
          </Table>
        </Box>
        
        {/* Pagination placeholder */}
        {ticketsData?.total > 0 && (
          <Flex justify="center" mt={4} mb={4}>
             <Button disabled={page === 1} onClick={() => setPage(page - 1)} mr={2}>Prev</Button>
             <Text alignSelf="center" mx={4}>Page {page} of {Math.ceil(ticketsData.total / pageSize)}</Text>
             <Button disabled={page >= Math.ceil(ticketsData.total / pageSize)} onClick={() => setPage(page + 1)}>Next</Button>
          </Flex>
        )}
      </Card>
    </Box>
  )
}
